import { useState } from 'react';
import { 
  Calendar, 
  Clock, 
  Plus, 
  User, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  UserCheck 
} from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import AppointmentForm from './AppointmentForm';
import { useGetAppointmentsCalender } from '../../services/query/appointment/appointment';
import AppointmentDetails from './AppointmentDetails';
import { Appointment } from '../../services/api/appointment/appointmentApi';
import { TIME_SLOTS } from '../../enums';

// const mockDoctors = [
//   { id: '1', name: 'Dr. Estrabillo', specialization: 'General Dentistry' },
//   { id: '2', name: 'Dr. Martinez', specialization: 'Orthodontics' },
//   { id: '3', name: 'Dr. Chen', specialization: 'Endodontics' },
//   { id: '4', name: 'Dr. Johnson', specialization: 'Periodontics' }
// ];

export default function AppointmentCalendar() {
  const { state } = useAppContext();
  const { appointments } = state;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>(TIME_SLOTS[0] ?? '09:00');

  // const appointmentsForDate = appointments.filter(apt => apt.date === selectedDate);

  const {data: appointmentsData} = useGetAppointmentsCalender({
    appointment_date: selectedDate
  });

  



  const getAppointmentForSlot = (time: string): Appointment | undefined => {
    return appointmentsData?.appointments.find(apt => apt.start_time.includes(time));
  };

  const createNewAppointment = (time: string): void => {
    setSelectedTimeSlot(time);
    setShowNewAppointmentModal(true);
  };

  // const handleCreateAppointment = (formData: {
  //   patientId: string;
  //   type: Appointment['type'];
  //   duration: number;
  //   notes: string;
  //   assignedDoctor?: string;
  // }) => {
  //   const patient = patients.find(p => p.id === formData.patientId);
  //   if (!patient) return;

  //   const newAppointment: Appointment = {
  //     id: Date.now().toString(),
  //     patientId: formData.patientId,
  //     patientName: patient.name,
  //     date: selectedDate,
  //     time: selectedTimeSlot,
  //     type: formData.type,
  //     status: 'scheduled',
  //     duration: formData.duration,
  //     notes: formData.notes,
  //     aiBooked: false,
  //     assignedDoctor: formData.assignedDoctor
  //   };

  //   dispatch({ type: 'ADD_APPOINTMENT', payload: newAppointment });
  //   setShowNewAppointmentModal(false);
  //   setSelectedTimeSlot('');
  // };


  const getAppointmentColor = (type: string) => {
    const normalizedType = type?.toLowerCase() ?? '';
    if (normalizedType.includes('emergency')) return 'bg-red-500 text-white';
    if (normalizedType.includes('procedure')) return 'bg-purple-500 text-white';
    if (normalizedType.includes('consultation')) return 'bg-orange-500 text-white';
    if (normalizedType.includes('cleaning')) return 'bg-blue-500 text-white';
    if (normalizedType.includes('followup')) return 'bg-green-500 text-white';
    return 'bg-gray-500 text-white';
  };

  //   const getAppointmentColor = (type: string) => {
  //   switch (type) {
  //     case 'emergency': return 'bg-red-500 text-white';
  //     case 'procedure': return 'bg-purple-500 text-white';
  //     case 'consultation': return 'bg-orange-500 text-white';
  //     case 'cleaning': return 'bg-blue-500 text-white';
  //     case 'followup': return 'bg-green-500 text-white';
  //     default: return 'bg-gray-500 text-white';
  //   }
  // };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'no-show': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const generateWeekDates = (baseDate: Date) => {
    const week = [];
    const startOfWeek = new Date(baseDate);
    startOfWeek.setDate(baseDate.getDate() - baseDate.getDay());

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      week.push(date);
    }
    return week;
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
  };

  const weekDates = generateWeekDates(currentDate);
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Appointments</h2>
            <p className="text-gray-600">Manage patient appointments and schedules</p>
          </div>
          <Calendar className="w-8 h-8 text-green-500" />
        </div>

        {/* Week Navigator */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateWeek('prev')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>

          <div className="flex space-x-2">
            {weekDates.map((date, index) => {
              const dateStr = date.toISOString().split('T')[0];
              const isSelected = dateStr === selectedDate;
              const isToday = dateStr === todayStr;
              const dayAppointments = appointments.filter(apt => apt.date === dateStr);

              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(dateStr)}
                  className={`flex flex-col items-center px-4 py-3 rounded-lg transition-all ${
                    isSelected
                      ? 'bg-green-600 text-white shadow-lg'
                      : isToday
                      ? 'bg-blue-50 text-blue-900 border-2 border-blue-300'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-xs font-medium">
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                  <span className="text-2xl font-bold my-1">
                    {date.getDate()}
                  </span>
                  <span className="text-xs">
                    {dayAppointments.length} apt{dayAppointments.length !== 1 ? 's' : ''}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => navigateWeek('next')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Daily Schedule */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">
            Schedule for {new Date(selectedDate).toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
          </h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">{appointmentsData?.total_appointments ?? 0} appointments</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 max-h-[600px] overflow-y-auto">
          {TIME_SLOTS.map((time) => {
            const appointment = getAppointmentForSlot(time);

            if (appointment) {
              return (
                <div
                  key={time}
                  onClick={() => setSelectedAppointment(appointment)}
                  className={`${getAppointmentColor(appointment.reason)} rounded-lg p-4 cursor-pointer hover:shadow-lg transition-all relative`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Clock className="w-4 h-4" />
                        <span className="font-bold">{time}</span>
                        {appointment.aiBooked && (
                          <span className="flex items-center space-x-1 px-2 py-0.5 bg-yellow-400 text-yellow-900 rounded-full text-xs font-semibold">
                            <Sparkles className="w-3 h-3" />
                            <span>AI Booked</span>
                          </span>
                        )}
                        <span className={`px-2 py-0.5 ${getStatusColor('confirmed')} rounded-full text-xs font-semibold`}>
                          {'scheduled'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mb-1">
                        <User className="w-4 h-4" />
                        <span className="font-semibold">{appointment.user_name}</span>
                      </div>
                 
                      {/* {appointment.assignedDoctor && ( */}
                        <div className="flex items-center space-x-1 mt-2 text-sm">
                          <UserCheck className="w-4 h-4" />
                          <span>{'Dr Estrabillo'}</span>
                        </div>
                      {/* )} */}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <button
                key={time}
                onClick={() => createNewAppointment(time)}
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-green-500 hover:bg-green-50 transition-all text-left group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-500 group-hover:text-green-600">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">{time}</span>
                  </div>
                  <Plus className="w-5 h-5 text-gray-400 group-hover:text-green-600" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Appointment Details Modal */}
      {selectedAppointment && (
        <AppointmentDetails
          appointment_date={selectedDate}
          selectedAppointment={selectedAppointment}
          statusColor={getStatusColor("bg-green-100 text-green-800" )}
          setSelectedAppointment={setSelectedAppointment}
        />
      )}

      {/* New Appointment Modal */}
      {showNewAppointmentModal && (
        <AppointmentForm
          initialDate={selectedDate}
          initialTime={selectedTimeSlot}
          onClose={() => {
            setShowNewAppointmentModal(false);
          }}
        />
      )}
    </div>
  );
}